import { includes, toLower, uniq, without } from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { usePageStateValue } from '../../../stores/PageStateStore';
import useDebounced from '../../../utilities/UseDebounced';

const ACTIONS = {
    EXPAND: 'expand',
    COLLAPSE: 'collapse',
    EXPAND_SITE: 'expandSite',
    COLLAPSE_SITE: 'collapseSite',
    ACTIVATE_SITE: 'activateSite',
};

const StateContext = createContext();
const StateDispatchContext = createContext();

export default function SidebarStateProvider(props) {
    const { children, id } = props;
    const hasRenderedRef = useRef(false);

    // handle all save/load features from URL
    const [encodedTablePageState, { setValue: setPageStateValue }] = usePageStateValue({ key: id });
    const setPageStateValueDeferred = useDebounced(setPageStateValue, 500);
    let decodedState;

    if (!hasRenderedRef.current) {
        hasRenderedRef.current = true;
        decodedState = decodeState(encodedTablePageState);
    }

    const [state, dispatch] = useReducer(reducer, null, () => buildInitialState(decodedState));

    useEffect(() => {
        setPageStateValueDeferred(id, encodeState(state));
    }, [id, state, setPageStateValueDeferred]);

    return (
        <StateContext.Provider value={state}>
            <StateDispatchContext.Provider value={dispatch}>{children}</StateDispatchContext.Provider>
        </StateContext.Provider>
    );
}

export function useSidebarState() {
    return useContext(StateContext);
}

export function useIsSiteExpanded(site) {
    const { expandedSites } = useSidebarState();

    return useMemo(() => includes(expandedSites, site.uuid), [expandedSites, site]);
}

export function SidebarStateConsumer(props) {
    const { children } = props;
    const state = useSidebarState();

    return children(state);
}

export function SidebarStateActionConsumer(props) {
    const { children } = props;
    const actions = useSidebarStateActions();

    return children(actions);
}

export function useSidebarStateActions() {
    const dispatch = useContext(StateDispatchContext);

    return useMemo(
        () => ({
            expandSite: (site) => dispatch({ type: ACTIONS.EXPAND_SITE, params: { site: site.uuid } }),
            collapseSite: (site) => dispatch({ type: ACTIONS.COLLAPSE_SITE, params: { site: site.uuid } }),
            activateSite: (site) => dispatch({ type: ACTIONS.ACTIVATE_SITE, params: { site: site.uuid } }),
            expand: () => dispatch({ type: ACTIONS.EXPAND }),
            collapse: () => dispatch({ type: ACTIONS.COLLAPSE }),
        }),
        [dispatch]
    );
}

function encodeState(state) {
    const { isExpanded, expandedSites } = state;

    return [isExpanded, expandedSites.map(encodeURIComponent).join(',')].map(encodeURIComponent).join('/');
}

function decodeState(stateStr) {
    if (!stateStr) {
        return {};
    }

    const [isExpanded, expandedSitesStr] = stateStr.split('/').map(decodeURIComponent);

    const expandedSites = expandedSitesStr.split(',').map(decodeURIComponent);

    return {
        isExpanded: toLower(isExpanded) === 'true',
        expandedSites,
    };
}

function reducer(state, action) {
    const { type, params } = action;

    switch (type) {
        case ACTIONS.EXPAND_SITE:
            return {
                ...state,

                isExpanded: true,
                activeSite: null,
                expandedSites: uniq([...state.expandedSites, params.site]),
            };

        case ACTIONS.ACTIVATE_SITE:
            return {
                ...state,

                isExpanded: true,
                activeSite: params.site,
            };

        case ACTIONS.COLLAPSE_SITE:
            return { ...state, expandedSites: without(state.expandedSites, params.site) };

        case ACTIONS.EXPAND:
            return { ...state, isExpanded: true };

        case ACTIONS.COLLAPSE:
            return { ...state, isExpanded: false };

        default:
            throw new Error(`Invalid reducer action type: ${type}`);
    }
}

function buildInitialState(initialState) {
    return {
        isExpanded: false,
        hoverSite: null,
        activeSite: null,
        expandedSites: [],

        ...(initialState || {}),
    };
}
